import { useMutation, useQuery } from '@apollo/client/react'
import {
  Button,
  EmptyState,
  ErrorState,
  FieldMessage,
  InlineConfirmation,
  LoadingState,
  PageLayout,
  Section,
  useLanguage,
  useTranslate,
} from '@athena/ui'
import { useState } from 'react'
import { usePageTitle } from '../app/page-title.js'
import {
  AcceptTutorConnectionRequestDocument,
  DeclineTutorConnectionRequestDocument,
  StudentConnectionRequestsDocument,
} from '../graphql/generated/operations.js'
import { formatDate } from '../lib/format.js'
import { useFirstUserErrorText } from '../lib/user-errors.js'

export function ConnectionRequestsScreen() {
  const t = useTranslate()
  const language = useLanguage()
  const userErrorText = useFirstUserErrorText()
  usePageTitle(t('connections.title'))
  const { data, loading, error } = useQuery(StudentConnectionRequestsDocument, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 15000,
  })
  const [accept, accepting] = useMutation(AcceptTutorConnectionRequestDocument, {
    refetchQueries: [StudentConnectionRequestsDocument],
    awaitRefetchQueries: true,
  })
  const [decline, declining] = useMutation(DeclineTutorConnectionRequestDocument, {
    refetchQueries: [StudentConnectionRequestsDocument],
    awaitRefetchQueries: true,
  })
  const [failure, setFailure] = useState<string>()
  const [confirmation, setConfirmation] = useState<string>()
  async function respond(requestId: string, approve: boolean) {
    setFailure(undefined)
    setConfirmation(undefined)
    try {
      const result = approve
        ? await accept({ variables: { requestId } })
        : await decline({ variables: { requestId } })
      const payload =
        result.data &&
        ('acceptTutorConnectionRequest' in result.data
          ? result.data.acceptTutorConnectionRequest
          : result.data.declineTutorConnectionRequest)
      if (!payload?.status) {
        setFailure(userErrorText(payload?.userErrors, t('connections.failed')))
        return
      }
      setConfirmation(t(approve ? 'connections.accepted' : 'connections.declined'))
    } catch {
      setFailure(t('connections.failed'))
    }
  }
  return (
    <PageLayout width="operational">
      <Section id="tutor-requests" title={t('connections.title')}>
        {loading && !data ? (
          <LoadingState className="p-0 py-4">{t('common.loading')}</LoadingState>
        ) : null}
        {error ? <ErrorState className="p-0 py-4">{t('connections.failed')}</ErrorState> : null}
        {data?.studentConnectionRequests.length === 0 ? (
          <EmptyState
            variant="section"
            title={t('connections.none')}
            description={t('connections.noneDescription')}
          />
        ) : null}
        {data?.studentConnectionRequests.length ? (
          <ul className="divide-y divide-border">
            {data.studentConnectionRequests.map((request) => (
              <li
                key={request.id}
                className="flex flex-col gap-4 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 flex-1">
                  <h3 className="text-h4 font-semibold">
                    {t('connections.fromTutor', [request.tutorName])}
                  </h3>
                  <p className="text-body-sm text-text-secondary break-all">{request.tutorEmail}</p>
                  <p className="mt-1 text-body-sm text-text-secondary">
                    {t('connections.requested', [formatDate(request.createdAt, language)])}
                  </p>
                </div>
                <div className="flex flex-wrap gap-3 sm:shrink-0 sm:justify-end">
                  <Button
                    variant="secondary"
                    disabled={accepting.loading || declining.loading}
                    onClick={() => void respond(request.id, false)}
                  >
                    {t('connections.decline')}
                  </Button>
                  <Button
                    disabled={accepting.loading || declining.loading}
                    onClick={() => void respond(request.id, true)}
                  >
                    {t('connections.accept')}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        ) : null}
        {failure ? <FieldMessage tone="error">{failure}</FieldMessage> : null}
        {confirmation ? <InlineConfirmation>{confirmation}</InlineConfirmation> : null}
      </Section>
    </PageLayout>
  )
}
